import React, { lazy } from 'react';
import { FadeIn } from './ui/FadeIn';
import { Button } from './ui/Button';
import { Check, RotateCcw, Eye, CalendarCheck, Sparkles, AlertCircle } from 'lucide-react';
import { trackAddToCart } from '../utils/metaPixel';
export function BonusSection() {
  const handlePurchaseClick = () => {
    trackAddToCart('Hábitos Atómicos Espirituales', 199, 'MXN');
    window.open('https://pay.hotmart.com/S104255087F?checkoutMode=10&bid=1770183530018', '_blank');
  };
  const painPoints = ['el día que fallas', 'el día que te distraes', 'el día que te da vergüenza volver', 'el día 4… cuando se muere la emoción'];
  const bonuses = [{
    id: 1,
    title: 'BONO #1 — Reinicio Express',
    subtitle: '(7 guiones para volver en 3 minutos)',
    value: '$147 MXN',
    image: 'https://cdn.magicpatterns.com/uploads/1gXCSkmYqAf6Sdo5PjbxrJ/BONO_1_Habtios_atomicos_espirituales.png',
    icon: <RotateCcw className="w-5 h-5 md:w-6 md:h-6 text-[#d4af37]" data-id="element-1" />,
    hook: 'Hay una frase que te destruye por dentro cuando fallas: "Ya lo arruiné." Y ahí es donde muere todo. Este bono existe para cortar ese momento exacto.',
    problem: 'En vez de caer en el ciclo: fallo → culpa → abandono → "empiezo el lunes"',
    solution: 'Vas a tener 7 guiones listos, palabra por palabra, para reiniciar en minutos. No para hacer un show. No para castigarte. Sino para volver con una acción mínima y seguir.',
    features: ['7 guiones para distintos escenarios', 'protocolo simple para volver sin drama', 'cierre rápido para terminar el día con una victoria mínima'],
    whyItWorks: 'Porque no te exige motivación. Te da lenguaje, estructura y un siguiente paso claro.'
  }, {
    id: 2,
    title: 'BONO #2 — Biblioteca de Señales',
    subtitle: '(21 disparadores visuales para no desaparecer)',
    value: '$127 MXN',
    image: 'https://cdn.magicpatterns.com/uploads/1hdxXUncmUq17mizjSGWQT/BONO_2_habitos_atomicos_espirituales.png',
    icon: <Eye className="w-5 h-5 md:w-6 md:h-6 text-[#d4af37]" data-id="element-2" />,
    hook: 'Tu mente se olvida. Tu día se te va. Y cuando te acuerdas… ya es tarde. Este bono convierte tu entorno en tu aliado.',
    solution: 'Vas a recibir una biblioteca de 21 señales diseñadas para activar el "Cue" del Método ANCLA: señales imprimibles para tu casa, señales para tu espejo, señales para tu escritorio, y fondos de pantalla para tu celular.',
    keyLine: 'Para que el hábito no dependa de "acordarte". Dependa de verlo.',
    features: ['21 disparadores visuales listos para imprimir y pegar', 'versión móvil (wallpapers)', 'mapa simple para instalarlos en 10 minutos'],
    whyItWorks: 'Porque tu ambiente te entrena. Y una señal visible le gana a una intención mental.'
  }, {
    id: 3,
    title: 'BONO #3 — Plan Anti-Abandono',
    subtitle: '(Semana Cero + 14 días de arranque imparable)',
    value: '$123 MXN',
    image: 'https://cdn.magicpatterns.com/uploads/4eUTeXzKtxUiNKHLVrENck/BONO_3_Habitos_atomicos.png',
    icon: <CalendarCheck className="w-5 h-5 md:w-6 md:h-6 text-[#d4af37]" data-id="element-3" />,
    hook: 'Si eres como la mayoría, tu peligro no es el día 1. Tu peligro es el día 4. Cuando ya no hay emoción. Cuando aparece la voz: "para qué".',
    solution: 'Este bono es un plan de implementación para arrancar sin caer en la trampa del "todo o nada". Primero haces la Semana Cero: instalas el sistema antes de exigir resultados. Luego sigues un calendario de 14 días con un ritmo diseñado para sostener, no para impresionar.',
    features: ['plantilla Semana Cero', 'calendario 14 días (simple, ejecutable)', 'plan de contingencia para volver rápido si fallas'],
    whyItWorks: 'Porque te da un camino que sobrevive al caos y a tu energía real.'
  }];
  const recapItems = ['eBook: Hábitos Atómicos Espirituales', 'Bono 1: Reinicio Express', 'Bono 2: Biblioteca de Señales', 'Bono 3: Plan Anti-Abandono'];
  return <section className="py-10 md:py-24 bg-[#0a1628] text-white overflow-hidden" data-id="element-4">
      <div className="max-w-6xl mx-auto px-5 md:px-6" data-id="element-5">
        {/* Intro Section */}
        <div className="max-w-3xl mx-auto text-center mb-10 md:mb-24" data-id="element-6">
          <FadeIn data-id="element-7">
            <h2 className="text-2xl md:text-5xl font-bold mb-4 md:mb-6 leading-tight" data-id="element-8">
              Espera… porque esto es lo que hace que la gente{' '}
              <span className="text-[#d4af37] italic" data-id="element-9">por fin lo sostenga</span>
            </h2>
            <p className="text-base md:text-xl text-gray-300 mb-4 md:mb-6" data-id="element-10">
              El problema no es que no sepas qué hacer. El problema es lo que
              pasa en la vida real:
            </p>
            <ul className="text-left max-w-md mx-auto space-y-2 mb-6 md:mb-8 bg-[#1e3a5f]/20 p-4 md:p-8 rounded-xl border border-[#1e3a5f]" data-id="element-11">
              {painPoints.map((point, i) => <li key={i} className="flex items-start text-gray-300 text-sm md:text-base" data-id="element-12">
                  <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-red-400 mr-2 md:mr-3 mt-1 flex-shrink-0" data-id="element-13" />
                  <span className="italic" data-id="element-14">{point}</span>
                </li>)}
            </ul>
            <p className="text-base md:text-xl text-gray-300 mb-3 md:mb-4" data-id="element-15">
              Por eso, además del eBook + Audiolibro + Habit Tracker, hoy te
              llevas 3 bonos diseñados para una sola misión:
            </p>
            <p className="text-2xl md:text-5xl font-serif text-[#d4af37] italic mb-4 md:mb-6" data-id="element-16">
              que no desaparezcas.
            </p>
            <p className="text-xs md:text-sm text-gray-400 uppercase tracking-widest" data-id="element-17">
              Y sí: estos bonos por sí solos podrían venderse aparte… pero hoy
              van incluidos en tu acceso de $199 MXN.
            </p>
          </FadeIn>
        </div>

        {/* Bonuses */}
        <div className="space-y-10 md:space-y-24 mb-12 md:mb-32" data-id="element-18">
          {bonuses.map((bonus, index) => <FadeIn key={bonus.id} delay={index * 0.05} data-id="element-19">
              <div id={index === 0 ? 'bonus-section' : undefined} className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-5 md:gap-12 items-center`} data-id="element-20">
                {/* Image Side */}
                <div className="w-full lg:w-1/2" data-id="element-21">
                  <div className="relative" data-id="element-22">
                    <img src={bonus.image} alt={bonus.title} loading="lazy" decoding="async" className="relative w-full rounded-lg shadow-xl md:shadow-2xl border border-[#d4af37]/20" data-id="element-23" />
                  </div>
                </div>

                {/* Content Side */}
                <div className="w-full lg:w-1/2 space-y-3 md:space-y-6" data-id="element-24">
                  <div className="flex items-center gap-2 md:gap-3 mb-1" data-id="element-25">
                    <div className="p-1.5 md:p-2 bg-[#d4af37]/10 rounded-lg border border-[#d4af37]/30" data-id="element-26">
                      {bonus.icon}
                    </div>
                    <h3 className="text-lg md:text-3xl font-bold text-[#d4af37]" data-id="element-27">
                      {bonus.title}
                    </h3>
                  </div>
                  <div className="flex items-center gap-2 -mt-1 md:-mt-2 mb-2" data-id="element-28">
                    <p className="text-sm md:text-lg text-gray-400 italic font-serif" data-id="element-29">
                      {bonus.subtitle}
                    </p>
                    <span className="text-[#d4af37] text-xs font-bold bg-[#d4af37]/10 px-2 py-0.5 rounded" data-id="element-30">
                      Valor: {bonus.value}
                    </span>
                  </div>

                  <div className="space-y-2 md:space-y-3 text-gray-300 text-sm md:text-base leading-relaxed" data-id="element-31">
                    <p className="font-medium text-white" data-id="element-32">{bonus.hook}</p>
                    {bonus.problem && <p data-id="element-33">{bonus.problem}</p>}
                    <p data-id="element-34">{bonus.solution}</p>
                    {bonus.keyLine && <p className="text-base md:text-xl text-[#d4af37] font-serif italic" data-id="element-35">
                        {bonus.keyLine}
                      </p>}
                  </div>

                  <div className="bg-[#1e3a5f]/30 rounded-xl p-3 md:p-6 border border-[#1e3a5f]" data-id="element-36">
                    <h4 className="text-xs md:text-sm uppercase tracking-widest text-[#d4af37] mb-2 md:mb-3 font-bold" data-id="element-37">
                      Lo que recibes:
                    </h4>
                    <ul className="space-y-1.5 md:space-y-2" data-id="element-38">
                      {bonus.features.map((feature, i) => <li key={i} className="flex items-start text-gray-300 text-sm md:text-base" data-id="element-39">
                          <Check className="w-4 h-4 md:w-5 md:h-5 text-[#d4af37] mr-2 md:mr-3 mt-0.5 flex-shrink-0" data-id="element-40" />
                          <span data-id="element-41">{feature}</span>
                        </li>)}
                    </ul>
                  </div>

                  <div className="border-l-4 border-[#d4af37] pl-3 md:pl-6 py-2" data-id="element-42">
                    <p className="text-xs md:text-sm text-[#d4af37] font-bold uppercase mb-1" data-id="element-43">
                      Por qué funciona:
                    </p>
                    <p className="text-gray-300 italic text-sm md:text-base" data-id="element-44">
                      {bonus.whyItWorks}
                    </p>
                  </div>
                </div>
              </div>
            </FadeIn>)}
        </div>

        {/* Recap Section */}
        <div id="recap-section" className="max-w-2xl mx-auto bg-gradient-to-b from-[#1e3a5f]/20 to-[#0a1628] border border-[#d4af37]/30 rounded-2xl p-5 md:p-12 text-center relative overflow-hidden" data-id="element-45">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#d4af37] to-transparent opacity-50" data-id="element-46"></div>
          <FadeIn data-id="element-47">
            <h3 className="text-xl md:text-3xl font-bold text-white mb-6 md:mb-8" data-id="element-48">
              Recap: lo que te llevas hoy{' '}
              <span className="text-[#d4af37]" data-id="element-49">($199 MXN)</span>
            </h3>

            <ul className="space-y-3 md:space-y-4 mb-8 md:mb-12 text-left max-w-md mx-auto" data-id="element-50">
              {recapItems.map((item, i) => <li key={i} className="flex items-center text-base md:text-lg text-gray-200" data-id="element-51">
                  <div className="w-5 h-5 md:w-6 md:h-6 rounded-full bg-[#d4af37] flex items-center justify-center mr-3 md:mr-4 flex-shrink-0" data-id="element-52">
                    <Check className="w-3 h-3 md:w-4 md:h-4 text-[#0a1628] stroke-[3]" data-id="element-53" />
                  </div>
                  {item}
                </li>)}
            </ul>

            <p className="text-lg md:text-xl text-gray-300 italic mb-6 md:mb-8 font-serif" data-id="element-54">
              No necesitas otro lunes. Necesitas un sistema para presentarte
              hoy.
            </p>

            <div className="flex flex-col items-center gap-3 md:gap-4" data-id="element-55">
              <Button className="w-full md:w-auto text-lg md:text-xl px-10 md:px-12 py-4 md:py-5 shadow-[0_0_20px_rgba(212,175,55,0.2)]" onClick={handlePurchaseClick} data-id="element-56">
                Empezar hoy
              </Button>
              <div className="flex items-center gap-2 text-gray-400 text-xs md:text-sm" data-id="element-57">
                <Sparkles className="w-4 h-4 text-[#d4af37]" data-id="element-58" />
                <span data-id="element-59">
                  Entra ahora y tu primera victoria es simple: no hacer cero.
                </span>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>;
}